import type { FunnelTags } from '@/utils/funnel'

/**
 * КОНВЕРТ СОБЫТИЯ AMPLITUDE — что уезжает с КАЖДЫМ событием, кроме его собственных свойств. Вынесен
 * из analytics.ts чистой функцией (2026-09-28), потому что с меткой сплит-теста у конверта появилось
 * правило, которое обязано проверяться без браузера: свойство по имени теста только у назначенных
 * пулом, у QA — нет (utils/funnel.ts, funnelTags). analytics.ts читает окружение сборки
 * (`import.meta.env`) и под Node не запускается, этот файл — запускается (utils/funnel.test.ts).
 *
 * Комментарии о том, ПОЧЕМУ каждое свойство едет с каждым событием, остались при них — ниже.
 */

type Value = string | number | boolean | null

export type EnvelopeContext = {
  environment: 'prod' | 'dev'
  arm: string
  experiment: string | null
  funnel: FunnelTags
  device: string
  timezone: string | null
  locale: string | null
  /** Метки объявления первого касания, только заполненные (utils/acquisition.ts). */
  acquisition: Record<string, string>
}

/** Типом-словарём, а не выведенным: имена свойства теста и тестов A/B приходят из окружения. */
export type EventEnvelope = {
  user_properties: {
    $setOnce: Record<string, string | number>
    $set: Record<string, string | number>
  }
  event_properties: Record<string, Value>
}

export const eventEnvelope = (ctx: EnvelopeContext, props?: Record<string, Value>): EventEnvelope => ({
  /* Ставится с КАЖДЫМ событием, а не только со входом: `$setOnce` пишет лишь при первой
     встрече, поэтому повтор ничего не стоит и ничего не перезаписывает — зато метка
     доезжает даже если самое первое событие сессии потерялось в полёте.
     ПЛЕЧО A/B — `$set`, не `$setOnce`: оно описывает, какой алгоритм человек видит СЕЙЧАС
     (см. utils/abVariant.ts), и в Amplitude режет любой отчёт, включая order_confirmed.
     `$setOnce` больше не бывает пустым: первое назначение воронки (first_funnel_type,
     first_split_pool) есть у каждого, и по нему отсеиваются перешедшие между кампаниями. */
  user_properties: {
    $setOnce: { ...ctx.acquisition, ...ctx.funnel.once },
    /* Имя теста рядом с буквой плеча (см. utils/abVariant.ts): a/b у разных тестов одни и те
       же, и только пара «тест × плечо» говорит, ЧТО человек видит. */
    $set: {
      ab_variant: ctx.arm,
      ...(ctx.experiment ? { ab_experiment: ctx.experiment, [ctx.experiment]: ctx.arm } : {}),
      /* Воронка — вторая ось рядом с плечом (utils/funnel.ts): плечо — какой экран
         человек видит, воронка — каким ПУТЁМ он идёт. Тоже `$set`: описывает текущее.
         Вместе с ней — тест и пул сплита и свойство по имени теста (funnelTags). */
      ...ctx.funnel.props,
    },
  },
  /* `device` идёт со всеми событиями, а не только со входом: по нему режется любая ступень
     воронки, и добавлять его в каждый вызов руками значило бы забыть в половине из них. */
  event_properties: {
    environment: ctx.environment,
    /* И как свойство события тоже: фильтр по событию не зависит от того, успело ли
       свойство пользователя примениться, и виден в сыром экспорте. */
    variant: ctx.arm,
    /* И СВОЙСТВО, НАЗВАННОЕ ИМЕНЕМ ТЕСТА, со значением-буквой: `onboarding_1stA/B: b`. В
       Amplitude тест ищется по своему имени в списке свойств и режется одним group-by, а
       прошлый тест (без такого свойства) в этот срез не попадает вовсе. Пара
       `experiment`/`variant` остаётся: по ней читается сырой экспорт и старые графики. */
    ...(ctx.experiment ? { experiment: ctx.experiment, [ctx.experiment]: ctx.arm } : {}),
    /* И как свойство события: воронка в Amplitude собирается группировкой по нему. Сплит-тест
       воронок — так же, как тест онбординга выше: `Funnel_1st_Test: 3`. */
    ...ctx.funnel.props,
    device: ctx.device,
    timezone: ctx.timezone,
    locale: ctx.locale,
    ...props,
  },
})
